import { useState } from 'react';
import { Empty, Select, Spin, Table, Tabs } from 'antd';
import dayjs from 'dayjs';
import ProcessFlowDiagram, { statusLabel } from './ProcessFlowDiagram';

function tabLabel(config) {
    const created = config.createdAt ? dayjs(config.createdAt).format('DD MMM YYYY HH:mm') : 'no date';
    return `${config.configName} · ${created}`;
}

function formatCount(value) {
    return (value || 0).toLocaleString();
}

function barPct(value, max) {
    if (!max || !value) return 0;
    return Math.max(6, (value / max) * 100);
}

function StageBoard({ stages, configName, onStageClick }) {
    const [sort, setSort] = useState('pending');
    const rows = [...(stages || [])].sort((left, right) => {
        if (sort === 'pending') return (right.pendingSkuCount || 0) - (left.pendingSkuCount || 0);
        return 0;
    });
    const maxPending = Math.max(0, ...rows.map(row => row.pendingSkuCount || 0));

    const open = row => {
        onStageClick?.({
            ...row,
            skuIds: row.pendingSkuIds || row.skuIds || [],
            title: `${configName} · ${row.stageName} · Pending`,
        });
    };

    return (
        <section className="reports-stage-board">
            <div className="reports-stage-board-head">
                <div>
                    <h5>Pending by stage</h5>
                    <span>Unique SKUs whose current CpId is this stage</span>
                </div>
                <Select
                    size="small"
                    value={sort}
                    onChange={setSort}
                    options={[
                        { value: 'pending', label: 'Most pending SKUs' },
                        { value: 'flow', label: 'Config order' },
                    ]}
                    style={{ minWidth: 180 }}
                />
            </div>
            <div className="reports-stage-cols">
                <span>Stage</span>
                <span>Pending SKUs</span>
                <span>Files</span>
            </div>
            {rows.map(row => (
                <div key={String(row.cpId)} className="reports-stage-row">
                    <div className="reports-stage-name">
                        <strong>{row.stageName || row.cpId || '—'}</strong>
                        <em>{row.cpId || '—'}</em>
                    </div>
                    <button type="button" className="reports-stage-metric is-pending" onClick={() => open(row)}>
                        <span className="reports-stage-track is-wide">
                            <span className="is-pending" style={{ width: `${barPct(row.pendingSkuCount, maxPending)}%` }} />
                        </span>
                        <b>{formatCount(row.pendingSkuCount)}</b>
                    </button>
                    <span className="reports-stage-files">{formatCount(row.fileCount)}</span>
                </div>
            ))}
            <p className="reports-stage-note">A SKU is counted at every stage where it currently has files.</p>
        </section>
    );
}

const statusColumns = [
    { title: 'Stage', dataIndex: 'stageName' },
    { title: 'CpId', dataIndex: 'currentCpId', width: 180, render: value => value || '—' },
    { title: 'Status', dataIndex: 'currentStatus', width: 180, render: statusLabel },
    { title: 'Files', dataIndex: 'fileCount', width: 90, render: value => (value || 0).toLocaleString() },
    { title: 'SKUs', dataIndex: 'skuCount', width: 90, render: value => (value || 0).toLocaleString() },
];

function ConfigPanel({ config, onStageClick }) {
    return (
        <div>
            <p className="reports-level2-meta">
                {config.fileCount.toLocaleString()} files · {config.skuCount.toLocaleString()} SKUs
                {config.clientName ? ` · ${config.clientName}` : ''}
            </p>
            <ProcessFlowDiagram
                processFlow={config.processFlow}
                stageCounts={config.stageCounts}
                onStageClick={stage =>
                    onStageClick?.({
                        ...stage,
                        title: `${config.configName} · ${stage.stageName}`,
                    })
                }
            />
            <StageBoard
                stages={config.stageCounts}
                configName={config.configName}
                onStageClick={onStageClick}
            />
            <h5 className="reports-level2-subtitle">Current status</h5>
            <Table
                size="small"
                rowKey={(row, index) => `${row.currentCpId}-${row.currentStatus}-${index}`}
                pagination={false}
                dataSource={config.statusSummary || []}
                columns={statusColumns}
            />
        </div>
    );
}

export default function FmsDeepDivePanel({ loading, payload, onStageClick }) {
    if (loading) {
        return (
            <div className="reports-level2-loading">
                <Spin />
            </div>
        );
    }
    if (!payload) {
        return <Empty description="Click Deep Dive on Shoot completion to load FMS statuses." />;
    }
    const configs = payload.configs || [];
    if (!configs.length) {
        return (
            <Empty
                description={`No FMS files found for shoot completed/partial SKUs (${payload.fmsFileCount || 0} file ids collected).`}
            />
        );
    }

    if (configs.length === 1) {
        return <ConfigPanel config={configs[0]} onStageClick={onStageClick} />;
    }

    return (
        <Tabs
            destroyInactiveTabPane
            items={configs.map(config => ({
                key: config.configId,
                label: tabLabel(config),
                children: <ConfigPanel config={config} onStageClick={onStageClick} />,
            }))}
        />
    );
}
